const { prepareBatchParams, toBatchShape } = require('../tools')

// Called by the NHLMIS (PSM) REST API
module.exports = getOrListBatch
module.exports.buildGenericParentMap = buildGenericParentMap
module.exports.convertToProductId = convertToProductId
async function getOrListBatch (fsApi, queryParams, alias) {
  const {userPrograms, ...restQueryParams} = queryParams

  const aliasOptions = alias !== 'none'
    ? {resolveAlias: {[alias]: true}}
    : {}
  const filters = prepareBatchParams(restQueryParams)
  const res = Object.values(filters).filter(Boolean).length
    ? await fsApi.batch.get(filters, aliasOptions)
      .then((batch) => [batch])
      .catch((_) => {
        // this likely means there is no parent product
        // for the requested batch
        return []
      })
    : await fsApi.batch.list()

  const products = await fsApi.product.listForPrograms(userPrograms)
  const productIds = products.map((p) => p._id)

  // Build a map of productId -> genericParent for manufacturer-specific products
  // This allows batches with manufacturer-specific productIds to be matched
  // via their generic parent product (see https://github.com/fielded/field-supply/issues/21174)
  const genericParentMap = await buildGenericParentMap(fsApi, res)

  const batches = res.filter((b) => {
    // Direct match: productId is in accessible products
    if (productIds.includes(b.productId)) {
      return true
    }

    // Check genericParent: manufacturer-specific products should match via their generic parent
    const genericParentProductId = genericParentMap.get(b.productId)
    if (genericParentProductId && productIds.includes(genericParentProductId)) {
      return true
    }

    return false
  })

  return batches.map((b) => toBatchShape(b, alias))
}

/**
 * Builds a map of productId -> genericParent productId for batches.
 * This allows manufacturer-specific products (e.g., product:ghsc-mal0027c)
 * to be matched via their generic parent (e.g., product:ghsc-mal0014).
 */
async function buildGenericParentMap (fsApi, batches) {
  const genericParentMap = new Map()

  // Get unique productIds from batches
  const uniqueProductIds = [...new Set(batches.map(b => b.productId))]

  if (uniqueProductIds.length === 0) {
    return genericParentMap
  }

  try {
    const productDocs = await fsApi.product.getByIds(uniqueProductIds)

    for (const product of productDocs) {
      if (product.genericParent) {
        // Convert genericParent (e.g., "GHSC-MAL0014") to product ID format (e.g., "product:ghsc-mal0014")
        const genericParentProductId = convertToProductId(product.genericParent)
        genericParentMap.set(product._id, genericParentProductId)
      }
    }
  } catch (err) {
    // If product lookup fails, continue without genericParent mapping
    // This ensures the endpoint still works, just without the manufacturer-specific matching
    console.error('Failed to build genericParent map:', err.message)
  }

  return genericParentMap
}

/**
 * Converts a genericParent value to a product ID format.
 * e.g., "GHSC-MAL0014" -> "product:ghsc-mal0014"
 */
function convertToProductId (genericParent) {
  if (!genericParent) return null
  const normalized = genericParent.trim().toLowerCase()
  if (!normalized) return null
  return normalized.startsWith('product:') ? normalized : `product:${normalized}`
}
